/* Shopable video -- hover-preview thumbnails + vertical TikTok-style reel lightbox.
   Native: CSS scroll-snap + lazy <template> videos + IntersectionObserver. No libraries. */
(function () {
  'use strict';
  var CAN_HOVER = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* --- Thumbnail: injects its <template> video on hover and plays it muted (desktop). --- */
  class SvHoverVideo extends HTMLElement {
    connectedCallback() {
      if (this._wired) return; this._wired = true;
      if (this.closest('.splide__slide--clone')) return;   // loop clones are poster-only -- no hover wiring, no video (the real twin plays)
      this.tpl = this.querySelector('template');
      var host = this.closest('shopable-video');
      if (host && host.getAttribute('data-card-play') === 'hover' && CAN_HOVER && !host.hasAttribute('data-center')) {
        var media = this.closest('.sv-card__media') || this;
        media.addEventListener('mouseenter', this._enter = () => this.preview(true));
        media.addEventListener('mouseleave', this._leave = () => this.preview(false));
      }
    }
    ensureVideo() {
      if (this.closest('.splide__slide--clone')) return null;   // never inject/fetch video into a loop clone -- poster only (prevents double-loading every clip)
      if (this._injected) return this._video;
      if (!this.tpl) return null;
      var v = this.tpl.content.firstElementChild.cloneNode(true);
      v.muted = true; v.setAttribute('preload', 'metadata');
      var self = this;
      v.addEventListener('playing', function () { self.setAttribute('data-vshow', ''); });   // crossfade: only fade the video in once it's ACTUALLY rendering frames (hides the first-frame pop). CSS gates the fade on .shopable-video--vfade.
      this.appendChild(v); this._injected = true; this._video = v; return v;
    }
    preview(on) {
      if (on) {
        var v = this.ensureVideo(); if (!v) return;
        this.setAttribute('data-playing', '');
        var p = v.play(); if (p && p.catch) p.catch(function () {});
      } else if (this._video) {
        this._video.pause();
        try { this._video.currentTime = 0; } catch (e) {}
        this.removeAttribute('data-playing');
        this.removeAttribute('data-vshow');   // fade back to the poster
      }
    }
    toggle() {
      var v = this.ensureVideo(); if (!v) return;
      if (v.paused) { this.setAttribute('data-playing', ''); var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      else { v.pause(); this.removeAttribute('data-playing'); this.removeAttribute('data-vshow'); }
    }
    toggleMute() {
      var v = this.ensureVideo(); if (!v) return;
      v.muted = !v.muted;
      this.toggleAttribute('data-unmuted', !v.muted);
    }
  }
  if (!customElements.get('sv-hover-video')) customElements.define('sv-hover-video', SvHoverVideo);

  /* Splide padding.right that shows a fractional next card (cols 1.5 -> half a card) and/or a px peek.
     Derivation: f = floor(cols) full cards + fraction p of the next visible in track width W:
     W = f*slide + f*gap + p*slide  ->  slide = (W - f*gap)/cols.  Splide sizes slides as
     (W - pad - (f-1)*gap)/f  ->  pad = W*(1 - f/cols) + gap*(f*f/cols - f + 1)  [+ peek px].
     Returned as calc() so it stays correct on resize without recomputing. */
  function svPeekPad(cols, gapPx, peekPx) {
    var f = Math.max(1, Math.floor(cols)), p = cols - f;
    if (p < 0.001) return peekPx > 0 ? peekPx : 0;
    var pct = (1 - f / cols) * 100;
    var px = gapPx * (f * f / cols - f + 1) + (peekPx || 0);
    return 'calc(' + pct.toFixed(4) + '% + ' + px.toFixed(2) + 'px)';
  }
  /* Same peek total, but SPLIT equally onto {left,right}. Center-play uses this so focus:center lands the
     active card on the VIEWPORT centre -- a right-only peek pushes the content box (and the centred card) left. */
  function svPeekPadHalf(cols, gapPx, peekPx) {
    var f = Math.max(1, Math.floor(cols)), p = cols - f;
    if (p < 0.001) return peekPx > 0 ? peekPx / 2 : 0;
    var pct = (1 - f / cols) * 100;
    var px = gapPx * (f * f / cols - f + 1) + (peekPx || 0);
    return 'calc(' + (pct / 2).toFixed(4) + '% + ' + (px / 2).toFixed(2) + 'px)';
  }

  /* --- Section wrapper: Splide drives the carousel (drag/momentum/snap/loop/autoplay); custom code adds
       center-play (enlarge+height-lock), the card-play modes, the sticky mini-player and opens the reel.
       HYBRID rule: Splide OWNS the .splide__list transform -- custom code only HOOKS splide.on()/splide.go(),
       it never sets the list transform. Custom transform engine (pos/drag/loop/geo) removed. --- */
  class ShopableVideo extends HTMLElement {
    connectedCallback() {
      if (this._wired) return; this._wired = true;
      this.track = this.querySelector('[data-sv-track]');             // becomes .splide__list
      this.vp = this.querySelector('[data-sv-window]') || this.track;  // becomes .splide__track
      this.tpl = this.querySelector('template[data-sv-lightbox]');
      var conn = navigator.connection; this._saveData = !!(conn && (conn.saveData || /\b[23]g\b/.test(conn.effectiveType || '')));
      // defer the Splide mount + wiring until the section is near the viewport -- cheaper initial load when far below the fold
      var self = this, boot = function () { if (self._booted) return; self._booted = true; self._boot(); };
      if ('IntersectionObserver' in window && this.getBoundingClientRect().top > window.innerHeight * 1.5) {
        var bio = new IntersectionObserver(function (es) { if (es.some(function (e) { return e.isIntersecting; })) { bio.disconnect(); boot(); } }, { rootMargin: '200% 0px' });
        bio.observe(this);
      } else boot();

      // Pause the active preview whenever the whole section leaves the viewport (it kept playing +
      // decoding while scrolled away); resume it when the section scrolls back in. The sticky player
      // is unaffected (it has its own element and its own IO).
      if ('IntersectionObserver' in window) {
        this._playIO = new IntersectionObserver((entries) => {
          entries.forEach((en) => {
            if (en.isIntersecting) {
              if (this._svOffPaused && this._svOffPaused.preview) { try { this._svOffPaused.preview(true); } catch (e) {} }
              this._svOffPaused = null;
            } else {
              var p = this.querySelector('sv-hover-video[data-playing]');
              if (p && p.preview) { p.preview(false); this._svOffPaused = p; }
            }
          });
        }, { threshold: 0 });
        this._playIO.observe(this);
      }
    }

    disconnectedCallback() {
      // Theme-editor re-renders replace the section DOM — free the Splide instance (its window
      // resize/matchMedia bindings otherwise pin the detached tree) and the body-reparented sticky.
      // queueMicrotask: an editor drag-REORDER detaches + re-attaches the SAME node synchronously —
      // only tear down when the node is truly gone. Storefront navigation never hits this mid-use.
      queueMicrotask(() => {
        if (this.isConnected) return;
        if (this.splide) { try { this.splide.destroy(true); } catch (e) {} this.splide = null; }
        if (this._playIO) { try { this._playIO.disconnect(); } catch (e) {} this._playIO = null; }
        // Companion window listeners added in _initCenter (center mode only; undefined otherwise) —
        // destroy(true) does NOT remove these, so drop them here to release the detached subtree.
        if (this._onLoadRecenter) { window.removeEventListener('load', this._onLoadRecenter); this._onLoadRecenter = null; }
        if (this._onPageShow) { window.removeEventListener('pageshow', this._onPageShow); this._onPageShow = null; }
        if (this._lockHeight) { window.removeEventListener('load', this._lockHeight); this._lockHeight = null; }
        if (this._stickyEl && this._stickyEl.parentNode === document.body) { try { this._stickyEl.remove(); } catch (e) {} }
      });
    }

    _boot() {
      this._createSplide();    // create the Splide instance (NOT mounted yet)
      this._wireOpen();        // tap a card -> open the reel (or inline toggle in 'click' mode)
      this._wireArrows();      // custom arrows -> splide.go  (registers on() hooks)
      // pagination dots are Splide-NATIVE now (see _createSplide) -- ARIA, keyboard and per-page dots for free
      this._initCenter();      // center-play: enlarge + autoplay + height-lock (hooks splide events)
      this._initFixedPeek();   // fixed-card-width + even-peek: recompute perPage/padding on resize (native options)
      this._initCenterFlick();  // center + fixed-width: keep 1-flick = 1-video by clamping to the live card count
      this._initCardModes();   // 'always' / touch-'hover' card autoplay (hooks splide visible/active)
      this._initSticky();
      // Reveal-when-centered (center mode): CSS keeps the viewport at opacity 0 (like the reference
      // carousel's skeleton) so the un-centered rail NEVER paints — measured: the fallback rail showed
      // ~900ms at -571px, and even mount() itself landed -336px off until the 400ms watchdog kicked in.
      // Here we re-check right after mount, refresh() in the same breath if off-centre, THEN reveal.
      // No/failed Splide or non-center mode -> reveal now (CSS also self-reveals after 2.5s, fail-safe).
      var self = this;
      var reveal = function () { self.classList.add('sv-centered'); };
      if (this.splide && this._center) {
        this.splide.on('mounted', function () {
          requestAnimationFrame(function () {
            try {
              var act = self.track.querySelector('.splide__slide.is-active');
              var tr = self.vp.getBoundingClientRect();
              var ar = act ? act.getBoundingClientRect() : null;
              if (ar && tr.width && Math.abs(ar.left + ar.width / 2 - (tr.left + tr.width / 2)) > 4) self.splide.refresh();
            } catch (e) {}
            requestAnimationFrame(reveal);
          });
        });
      } else reveal();
      // mount LAST so every on('mounted'|'overflow'|'active'|'resized') hook registered above fires on the initial
      // mount -- registering after mount() misses those one-shot events (that hid the arrows/dots/height-lock init).
      if (this.splide) this.splide.mount();
    }

    _cards() { return this.track ? Array.prototype.slice.call(this.track.querySelectorAll('.sv-card')) : []; }
    _realCards() { return this._cards().filter(function (c) { return !c.classList.contains('splide__slide--clone'); }); }
    _activeCard() { return this.track && this.track.querySelector('.splide__slide.is-active:not(.splide__slide--clone)'); }   // loop mode also marks clones is-active -- we want the real slide

    _createSplide() {
      if (!window.Splide || !this.track || !this.vp) { this._noSplide = true; return; }  // graceful: fallback CSS keeps a static laid-out row
      var self = this;
      this._center = this.hasAttribute('data-center');
      this._loop = this.getAttribute('data-loop') === 'true';
      var root = this.querySelector('.shopable-video__viewport') || this.vp.parentElement;
      // add Splide DOM classes AT RUNTIME (not in liquid) so the section is never hidden by Splide's :not(.is-initialized) CSS if the JS is slow/absent
      root.classList.add('splide');
      this.vp.classList.add('splide__track');
      this.track.classList.add('splide__list');
      Array.prototype.forEach.call(this.track.children, function (c) { if (c.classList && c.classList.contains('sv-card')) c.classList.add('splide__slide'); });
      var cs = getComputedStyle(this);
      var colsD = parseFloat(cs.getPropertyValue('--sv-cols-d')) || 5;
      var colsM = parseFloat(cs.getPropertyValue('--sv-cols-m')) || 2;
      var gapD = (cs.getPropertyValue('--sv-gap-d') || '16px').trim();
      var gapM = (cs.getPropertyValue('--sv-gap-m') || '10px').trim();
      var peekD = parseFloat(cs.getPropertyValue('--sv-peek-d')) || 0;
      var cardwD = parseFloat(cs.getPropertyValue('--sv-cardw-d')) || 0;   // desktop "fixed card width" mode (0 = use columns). >0 => Splide's NATIVE fixedWidth: card stays this px wide and the NUMBER of cards adapts to the viewport (PupRing / real-reel behaviour), instead of a fixed column count that shrinks cards on narrow screens.
      // Splide's perPage is integer-only: fractional columns (mobile "1.5 to peek") and the desktop
      // "Preview of next card" px both become the NATIVE padding.right option -- Splide subtracts the
      // padding before sizing slides, which lands on the exact same card width as the pre-mount CSS
      // flex-basis formula (parseInt used to floor 1.5 -> 1 full-width card, and peek died at mount).
      var perPageM = Math.max(1, Math.floor(colsM));
      var perPageD = Math.max(1, Math.floor(colsD));
      var padM = svPeekPad(colsM, parseFloat(gapM) || 0, 0);
      var padD = svPeekPad(colsD, parseFloat(gapD) || 0, peekD);
      var opts = {
        type: this._loop ? 'loop' : 'slide',
        label: this.getAttribute('data-a11y-label') || 'Video carousel',   // native region label -- Splide owns the carousel ARIA on its root
        perPage: perPageM, perMove: 1, gap: gapM,          // base = mobile (mediaQuery:'min')
        padding: { left: 0, right: padM },                 // fractional-column peek (0 when cols is whole)
        focus: this._center ? 'center' : 0,
        arrows: false, drag: true,
        pagination: this.hasAttribute('data-pagination'),  // NATIVE pagination (ARIA + keyboard). NOTE: focus is set (0/'center') -> Splide is in "focus mode", so dots are PER-POSITION (getEnd()+1 dots, all functional via omitEnd), not per-page
        classes: { pagination: 'sv-dots', page: 'sv-dot' },   // OWN class names only -- NOT splide__pagination/__page, whose theme-global rules in base.css (transparent bg, ::before-painted dots, counters, absolute positioning) would fight our dots. Splide core reads these from options and holds element refs, so functionality doesn't need the default class.
        speed: 420, easing: 'cubic-bezier(.25,1,.5,1)',   // Splide's built-in reducedMotion drops speed to 0 for prefers-reduced-motion users
        flickMaxPages: 1, updateOnMove: true, waitForTransition: false,
        trimSpace: !this._center && !this._loop, omitEnd: !this._center && !this._loop,   // both are slide-type-only options (loop has no edges)
        mediaQuery: 'min',
        breakpoints: { 750: { perPage: perPageD, gap: gapD, padding: { left: 0, right: padD } } },   // padding set on BOTH tiers -- breakpoints merge over base, a desktop without it would inherit the mobile pad
      };
      if (cardwD > 0) {
        this._fixedW = true;
        this._ppM = perPageM; this._padM = padM;   // mobile fallback for the even-peek resizer
        var fpeek = parseFloat(cs.getPropertyValue('--sv-fpeek')) || 0;   // % of the next card to always reveal (0 = off)
        if (fpeek > 0) {
          // EVEN-PEEK: keep a CONSTANT fraction of the next card visible at every width. Pure fixedWidth peeks
          // by whatever's left over (0 at some widths -> looks un-scrollable); here we pick the card COUNT from
          // the target width and let the width flex a hair so the peek is always the same. All via NATIVE Splide
          // options (perPage + padding), recomputed on the native 'resized' hook -- no fixedWidth here.
          // Drop the breakpoint perPage; ZERO its padding (not delete) so the mobile fractional-column peek
          // padding on the BASE options can't leak up to desktop (a 2.5 columns_mobile made a calc(20%) pad
          // that shifted the whole rail + broke centering). The resizer owns desktop sizing via fixedWidth.
          delete opts.breakpoints[750].perPage;
          opts.breakpoints[750].padding = { left: 0, right: 0 };
          this._evenPeek = { t: cardwD, f: fpeek / 100, g: parseFloat(gapD) || 0 };
        } else {
          // NATIVE Splide fixedWidth on desktop: each card = cardwD px, Splide fits floor(viewport/card) and
          // scrolls the rest, the trailing partial card peeks on its own -> responsive count like PupRing.
          // Applied ONLY to the 750+ breakpoint; mobile keeps its column count. fixedWidth overrides perPage
          // for sizing, so drop perPage/padding here (peek is intrinsic; padding would double-count).
          opts.breakpoints[750].fixedWidth = cardwD + 'px';
          delete opts.breakpoints[750].perPage;
          opts.breakpoints[750].padding = { left: 0, right: 0 };   // ZERO (not delete): stop the mobile fractional-column peek padding on the BASE options leaking up to desktop and shifting/off-centering the rail
        }
      }
      if (this._center) {
        // Center-play centres the ACTIVE card. A right-only peek pad (from a fractional columns_mobile like 2.5)
        // shifts the content box left, so focus:center lands the card ~half-the-pad off the viewport centre.
        // Split the peek to BOTH sides on the tiers that still use padding (mobile always; desktop only in
        // columns mode -- fixed-width desktop already centres via padding 0 / fixedWidth).
        opts.padding = { left: svPeekPadHalf(colsM, parseFloat(gapM) || 0, 0), right: svPeekPadHalf(colsM, parseFloat(gapM) || 0, 0) };
        if (!this._fixedW) {
          var phD = svPeekPadHalf(colsD, parseFloat(gapD) || 0, peekD);
          opts.breakpoints[750].padding = { left: phD, right: phD };
        }
        // one flick/drag = ONE video through the centre. flickMaxPages counts PAGES (= perPage cards), so the
        // default 1 lets a light flick fly past a whole page -- clamp the flick distance to a single card.
        opts.flickMaxPages = 1 / perPageM;
        if (this._fixedW) {
          // fixed-card-width: the ACTUAL cards-per-view is set by fixedWidth and changes with the viewport,
          // so a static 1/perPageD clamp overshoots (a hard flick jumped ~2 cards at 3440px). Leave the desktop
          // clamp to _initCenterFlick(), which sets it from the live card count on mount + resize.
          this._gapDpx = parseFloat(gapD) || 0;
        } else {
          opts.breakpoints[750].flickMaxPages = 1 / perPageD;
        }
      }
      if (this._center && this.hasAttribute('data-swipe-single')) {
        // "Snap swipe": Splide's NATIVE free-drag physics (the same feel as the Embla-based reference we
        // frame-traced): drag 'free' keeps the release velocity and decelerates via the Scroll component,
        // snap:true lands the glide on a card, flickPower scales the glide distance, flickMaxPages caps it.
        // The "Swipe snap strength" slider maps to flickPower: 0% -> 900 loose/far, 50% -> 510 (the docs-
        // recommended ~500), 100% -> 120 (a flick barely clears the next card).
        var sw = parseInt(this.getAttribute('data-swipe-strength'), 10); if (isNaN(sw)) sw = 50;
        sw = Math.max(0, Math.min(100, sw)) / 100;
        opts.drag = 'free';
        opts.snap = true;
        opts.flickPower = Math.round(900 - 780 * sw);
        // NOTE: flickMaxPages is IGNORED whenever drag:'free' — the bundled Splide caps the flick
        // distance only in PAGED drag mode (free mode uses Infinity), so the old "3-card cap" here
        // was dead code. The glide distance is therefore bounded by flickPower alone: the strength
        // slider fully owns the feel (0% = far glide, 100% = barely clears the next card).
      }
      var interval = parseInt(this.getAttribute('data-autoscroll'), 10);  // "auto-scroll every Nms"
      if (interval) { opts.autoplay = true; opts.interval = interval; opts.pauseOnHover = true; opts.pauseOnFocus = true; }
      try { this.splide = new Splide(root, opts); }   /* mount() happens at the end of _boot(), after all on() hooks are registered */
      catch (e) { this._noSplide = true; this.splide = null; }
    }

    _wireOpen() {
      // Splide-NATIVE slide clicks: splide.on('click') fires for real slides AND loop clones (per-card listeners
      // wired pre-mount missed the clones -> every card left of the first video was dead), hands us the original
      // index via Slide.slideIndex, and Splide's Drag component already swallows the ghost click after a drag.
      // The delegated fallback below only runs when Splide is absent (static rail).
      var self = this;
      if (this.splide) {
        this.splide.on('click', function (Slide, e) {
          self._cardClick(e, Slide.slide, Slide.slideIndex > -1 ? Slide.slideIndex : Slide.index);
        });
      } else {
        this.addEventListener('click', function (e) {
          var card = e.target.closest && e.target.closest('.sv-card');
          if (!card || !self.contains(card)) return;
          var media = card.querySelector('[data-sv-open]');
          self._cardClick(e, card, media ? (parseInt(media.getAttribute('data-sv-open'), 10) || 0) : 0);
        });
      }
      // keyboard: the media div advertises role="button" tabindex="0" -- honour it (Enter/Space = activate).
      // Delegated on the root so clones and re-mounts are covered; Space's default page-scroll is prevented.
      this.addEventListener('keydown', function (e) {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        var el = e.target.closest && e.target.closest('[data-sv-open]');
        if (!el || !self.contains(el)) return;
        e.preventDefault();
        self._cardClick(e, el.closest('.sv-card'), parseInt(el.getAttribute('data-sv-open'), 10) || 0);
      });
    }

    // one click brain per card, wherever the event came from (Splide's click event or the fallback delegation)
    _cardClick(e, card, index) {
      var hv = card.querySelector('sv-hover-video');
      var btn = e.target.closest && e.target.closest('[data-sv-cardplay], [data-sv-cardmute]');
      if (btn) {   // the card control buttons own their clicks (honour the "Card play" mode; 'reel' opens the fullscreen reel)
        e.preventDefault(); e.stopPropagation();
        if (btn.hasAttribute('data-sv-cardmute')) {
          if (card.classList.contains('splide__slide--clone')) {   // clones are poster-only — mute the REAL twin that's actually playing (the clone's own toggle was a silent no-op)
            var realCard = this._realCards()[index];
            hv = realCard && realCard.querySelector('sv-hover-video');
          }
          if (hv && hv.toggleMute) hv.toggleMute();
          return;
        }
        if (this.getAttribute('data-card-play') === 'reel') { this.open(index); return; }
        if (card.classList.contains('splide__slide--clone')) {
          // poster-only clone can't inline-play (sv-hover-video never injects video there) -- centre its real twin; center-mode autoplay takes over
          if (this.splide) this.splide.go(index);
          return;
        }
        if (hv && hv.toggle) hv.toggle();
        return;
      }
      if (!(e.target.closest && e.target.closest('[data-sv-open]'))) return;   // click landed outside the media (product link, caption...) -- leave it alone
      e.preventDefault();
      if (this.getAttribute('data-card-play') === 'click' && !this._center) {  // 'click' mode = play the muted preview inline instead of opening the reel
        if (hv && hv.toggle && !card.classList.contains('splide__slide--clone')) { hv.toggle(); return; }
      }
      this.open(index);
    }

    _wireArrows() {
      var self = this;
      this.prevBtn = this.querySelector('[data-sv-prev]');
      this.nextBtn = this.querySelector('[data-sv-next]');
      if (this.prevBtn) this.prevBtn.addEventListener('click', function () { if (self.splide) self.splide.go('<'); });
      if (this.nextBtn) this.nextBtn.addEventListener('click', function () { if (self.splide) self.splide.go('>'); });
      if (!this.splide) return;
      // Hide prev at the very start and next at the very end (non-loop) -- omitEnd:true makes getEnd() the LAST
      // reachable index (no dead-zone past the content edge). Update on every move so the arrows never sit "dead".
      var upd = function () {
        var C = self.splide.Components.Controller, end = C.getEnd();
        var overflow = self._loop || end > 0;   // end>0 -> there is somewhere to scroll
        if (self.prevBtn) self.prevBtn.hidden = !overflow || (!self._loop && self.splide.index <= 0);
        if (self.nextBtn) self.nextBtn.hidden = !overflow || (!self._loop && self.splide.index >= end);
      };
      this.splide.on('mounted move moved overflow updated resized', upd);
    }

    // Even-peek sizing: keep a constant fraction of the next card visible at every viewport width.
    // Card COUNT is derived from the target card width; the width flexes slightly so the peek stays even.
    // Runs on Splide's native 'mounted'/'resized' hooks and drives NATIVE perPage + padding options only.
    _initFixedPeek() {
      if (!this._evenPeek || !this.splide) return;
      var self = this, ep = this._evenPeek;
      var mql = window.matchMedia('(min-width: 750px)');   // cache the MediaQueryList (don't rebuild it on every resize tick)
      var apply = function () {
        self._epRaf = 0;
        var W = self.vp.getBoundingClientRect().width; if (!W) return;
        var desktop = mql.matches;
        var sig;   // signature so we only refresh when the target actually changes (also breaks resized->refresh->resized loops)
        if (desktop) {
          // cards begin at the track's LEFT padding and the trailing peek shows THROUGH the right padding to
          // the viewport edge -> the span for "n cards + peek" is W minus the LEFT gutter. Re-read the gutter
          // every time: the theme's page-width padding changes across breakpoints, so a value cached at mount
          // would drift the peek after a resize.
          var gut = parseFloat(getComputedStyle(self.vp).paddingLeft) || 0;
          var W2 = W - gut;
          // n full cards nearest the target width, then set fixedWidth so exactly n full + f partial fill W2.
          // fixedWidth = (W2 - n*gap)/(n+f) makes the leftover after n cards equal f*card -> a CONSTANT peek.
          var n = Math.max(1, Math.round((W2 - ep.t * ep.f) / (ep.t + ep.g)));
          var card = Math.round((W2 - n * ep.g) / (n + ep.f));
          sig = 'd' + card;
          if (sig === self._epSig) return;
          self._epSig = sig;
          self.splide.options = { fixedWidth: card + 'px' };
        } else {
          sig = 'm';
          if (sig === self._epSig) return;
          self._epSig = sig;
          self.splide.options = { fixedWidth: 0, perPage: self._ppM };   // mobile: back to column count
        }
        self.splide.refresh();   // sizing options recompute on a full refresh; the signature guard stops the loop
      };
      // Coalesce bursts of 'resized' into ONE apply per animation frame: a fast window drag fires 'resized' many
      // times per frame, and each apply reads layout (getBoundingClientRect + getComputedStyle) -> without this,
      // that's read/write/read thrashing during the drag. rAF batches them; the sig guard still skips no-op refreshes.
      var schedule = function () { if (!self._epRaf) self._epRaf = requestAnimationFrame(apply); };
      this.splide.on('mounted', apply);     // first layout: run immediately (no 1-frame delay on first paint)
      this.splide.on('resized', schedule);  // resizes: batched
    }

    // center + fixed-card-width: keep "one flick = one video". fixedWidth makes the cards-per-view change with
    // the viewport, so the static flickMaxPages set in _createSplide overshoots on wide screens. Recompute it
    // from the LIVE card count on mount + resize (flickMaxPages is read live by Splide's Drag, so no refresh).
    _initCenterFlick() {
      // Skip under swipe-single: that path uses drag:'free', where Splide caps the flick distance at
      // Infinity and NEVER reads flickMaxPages (see the note in _createSplide ~265-268). Running this
      // handler there would only do wasted per-'resized' getBoundingClientRect() reads + no-op writes.
      if (!this._center || !this._fixedW || this.hasAttribute('data-swipe-single') || !this.splide) return;
      var self = this, gap = this._gapDpx || 0;
      var mql = window.matchMedia('(min-width: 750px)');
      var apply = function () {
        if (!mql.matches) { self.splide.options.flickMaxPages = 1 / (self._ppM || 1); return; }   // mobile keeps its column clamp
        var W = self.vp.getBoundingClientRect().width; if (!W) return;
        var media = self.track.querySelector('.splide__slide.is-active .sv-card__media') || self.track.querySelector('.sv-card__media');
        if (!media) return;
        var cw = media.getBoundingClientRect().width; if (!cw) return;
        var eff = Math.max(1, Math.round(W / (cw + gap)));   // how many fixed-width cards are actually on screen now
        self.splide.options.flickMaxPages = 1 / eff;         // -> a hard flick travels at most ~one card
      };
      this.splide.on('mounted resized', apply);
    }

    // center-play: the active (centered) card enlarges (CSS keys on Splide's own .is-active) + autoplays; the rail height
    // is locked to the tallest card so the section below never jumps as the active card animates taller.
    _initCenter() {
      if (!this._center || !this.splide) return;
      var self = this, playTimer;
      // Self-healing recenter: back/forward navigation (and other layout races) can leave the focus:center
      // position stale -- reproduced: goBack() landed the rail ~3 cards off-centre. While the carousel is
      // IDLE, measure the active card's centre vs the track centre; >4px off -> refresh() re-lays-out and
      // repositions. Runs after mount, on full page load, and on pageshow (bfcache restores).
      var recenter = function () {
        if (!self.splide || !self.splide.state.is(3)) return;   // 3 = IDLE (never fight a move/scroll/drag)
        var act = self.track.querySelector('.splide__slide.is-active');
        var tr = self.vp.getBoundingClientRect();
        if (!act || !tr.width) return;
        var ar = act.getBoundingClientRect();
        if (Math.abs(ar.left + ar.width / 2 - (tr.left + tr.width / 2)) > 4) self.splide.refresh();
      };
      this.splide.on('mounted', function () { setTimeout(recenter, 400); setTimeout(recenter, 1500); });
      // Stored as fields so disconnectedCallback can removeEventListener them — else an editor section
      // re-render leaves these window listeners (closing over `self`) pinning the detached subtree, even
      // after splide.destroy(true) (which only frees Splide's OWN bindings). Mirrors the reel's teardown().
      this._onLoadRecenter = recenter;
      this._onPageShow = function (e) { if (e.persisted) setTimeout(recenter, 120); };
      window.addEventListener('load', this._onLoadRecenter);
      window.addEventListener('pageshow', this._onPageShow);
      var autoplayActive = function () {
        var card = self._activeCard(); if (!card) return;
        self._realCards().forEach(function (c) { if (c !== card) { var pv = c.querySelector('sv-hover-video'); if (pv && pv.preview) pv.preview(false); } });
        clearTimeout(playTimer);
        playTimer = setTimeout(function () {   // DEFER the play until the swipe settles -- a fast swipe crosses many cards; only the one you land on plays
          if (self._activeCard() !== card || self._saveData) return;
          var hv = card.querySelector('sv-hover-video'); if (hv && hv.preview) hv.preview(true);
        }, 160);
      };
      this.splide.on('active', autoplayActive);
      this._lockHeight = function () {   // fix the list height to the tallest (active-sized) card so it can't grow/shrink -> the section below never jumps
        // batched write -> read -> write phases: the old per-card toggle-and-measure interleaved writes with
        // reads, forcing ~2 synchronous reflows PER CARD (clones included) on every 'resized'. Batching costs
        // 2 reflows per RUN total. Cards are measured all-active at once -- aspect-ratio is per-card, so the
        // heights are identical to measuring one at a time.
        var cards = self._cards(), toggled = [], medias = [];
        cards.forEach(function (c) {   // phase 1: writes only
          var media = c.querySelector('.sv-card__media');
          if (media) { media.style.transition = 'none'; medias.push(media); }   // --scale has a .45s aspect-ratio transition -- measure the FINAL enlarged height, never a mid-animation frame
          if (!c.classList.contains('is-active')) { c.classList.add('is-active'); toggled.push(c); }
        });
        var maxH = 0;
        cards.forEach(function (c) { var h = c.offsetHeight; if (h > maxH) maxH = h; });   // phase 2: reads only (first read reflows once for the whole batch)
        toggled.forEach(function (c) { c.classList.remove('is-active'); });                // phase 3: writes
        void self.track.offsetHeight;                                                      // settle the un-toggle while transitions are still off, so restoring them can't animate
        medias.forEach(function (m) { m.style.transition = ''; });
        if (maxH > 40) self.track.style.height = Math.ceil(maxH) + 'px';
      };
      this.splide.on('mounted resized', this._lockHeight);
      // the 'mounted' pass can catch the first paint before slide widths/aspect-ratio CSS settle (with --scale it
      // locked a rail ~2x too tall) -- re-measure once the layout is final, and again on full page load (late fonts/media)
      this.splide.on('mounted', function () { setTimeout(self._lockHeight, 500); });
      window.addEventListener('load', this._lockHeight);
      requestAnimationFrame(autoplayActive);
      // side-card play / mute buttons: no wiring needed here -- button clicks bubble to the slide root, so
      // Splide's native 'click' event catches them (clones included) and _cardClick routes them (see _wireOpen)
    }

    // non-center card autoplay modes. 'always' = play EVERY card Splide marks .is-visible (off-screen stay paused/unloaded).
    // 'hover' on touch (no hover) = play the active card. Both hook Splide's own visibility/active state (no custom pos math).
    _initCardModes() {
      if (this._center || !this.splide) return;
      var self = this, mode = this.getAttribute('data-card-play');
      if (mode === 'always') {
        var apply = function () {
          if (self._saveData) return;
          self._cards().forEach(function (c) {
            if (c.classList.contains('splide__slide--clone')) return;
            var play = c.classList.contains('is-visible');
            if (play === !!c._svOn) return; c._svOn = play;
            var hv = c.querySelector('sv-hover-video'); if (hv && hv.preview) hv.preview(play);
          });
        };
        this.splide.on('visible hidden mounted moved', apply);
        requestAnimationFrame(apply);
      } else if (mode === 'hover' && !CAN_HOVER) {
        this.splide.on('active', function () {
          if (self._saveData) return;
          var card = self._activeCard();
          self._realCards().forEach(function (c) { if (c !== card) { var pv = c.querySelector('sv-hover-video'); if (pv && pv.preview) pv.preview(false); } });
          if (card) { var hv = card.querySelector('sv-hover-video'); if (hv && hv.preview) setTimeout(function () { if (self._activeCard() === card) hv.preview(true); }, 160); }
        });
      }
    }

    // sticky floating mini-player: appears once the section is scrolled past; click opens the reel
    _initSticky() {
      var el = this.querySelector('[data-sv-sticky]');
      if (!el) return;
      if (el.parentNode !== document.body) document.body.appendChild(el);   // fixed positioning must not sit under a transformed ancestor
      var vid = el.querySelector('.sv-sticky__video');
      var mqMobile = window.matchMedia('(max-width: 749px)');
      var dismissed = false, shown = false;
      var show = () => {
        if (dismissed || shown) return;
        if (el.classList.contains('sv-sticky--desktop_only') && mqMobile.matches) return;
        shown = true;
        el.hidden = false;
        requestAnimationFrame(() => el.classList.add('is-visible'));
        if (vid && !this._saveData) {
          if (!vid.getAttribute('src') && vid.dataset.src) vid.setAttribute('src', vid.dataset.src);
          var p = vid.play(); if (p && p.catch) p.catch(function () {});
        }
      };
      var hide = () => {
        if (!shown) return;
        shown = false;
        el.classList.remove('is-visible');
        if (vid) vid.pause();
      };
      var io = new IntersectionObserver((entries) => {
        entries.forEach((en) => {
          if (!en.isIntersecting && en.boundingClientRect.top < 0) show(); else hide();
        });
      }, { threshold: 0 });
      io.observe(this);
      this._stickyEl = el;   // tracked so disconnectedCallback can remove the body-reparented orphan (editor re-renders)
      var openEl = el.querySelector('[data-sv-sticky-open]');
      if (openEl) {
        var go = (e) => { e.preventDefault(); hide(); this.open(parseInt(el.getAttribute('data-index'), 10) || 0); };
        openEl.addEventListener('click', go);
        // it advertises role="button" tabindex="0" — honour it (it lives on <body> now, outside the section's delegated keydown)
        openEl.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') go(e); });
      }
      var closeEl = el.querySelector('[data-sv-sticky-close]');
      if (closeEl) closeEl.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); dismissed = true; hide(); });
    }

    open(index) {
      if (!this.tpl) return;
      // Replacing a still-open reel (e.g. the corner mini-player is docked and the shopper taps another card):
      // tear the old one down FIRST so its Splide instance + window 'resize' listener don't leak (a leaked
      // listener would later call refresh() on a detached DOM). Falls back to remove() for a non-lightbox stray.
      var existing = document.querySelector('.sv-lightbox');
      if (existing) { if (existing._svLightbox) existing._svLightbox.teardown(); existing.remove(); }
      // The reel covers the page — pause the carousel's own preview (it kept decoding behind the
      // opaque overlay for the whole session) and the sticky player; close() resumes them.
      var playingHv = this.querySelector('sv-hover-video[data-playing]');
      if (playingHv && playingHv.preview) { playingHv.preview(false); this._svResumeHv = playingHv; }
      var stVid = document.querySelector('.sv-sticky.is-visible .sv-sticky__video');
      if (stVid) stVid.pause();
      var frag = this.tpl.content.firstElementChild.cloneNode(true);
      frag._svHost = this;   // lets the lightbox resume this section's preview on close
      var mm = this.getAttribute('data-mini');
      var onMobile = window.matchMedia('(max-width: 749px)').matches;
      frag.classList.add('sv-mini-ok');
      if (mm === 'all' || (mm === 'desktop' && !onMobile) || (mm === 'mobile' && onMobile))
        frag.classList.add('sv-lightbox--mini');
      document.body.appendChild(frag);
      new SvLightbox(frag, index, document.activeElement, this.hasAttribute('data-reel-autoplay'), this.hasAttribute('data-reel-autoadvance'), this.hasAttribute('data-reel-sound'), this.getAttribute('data-loop') === 'true');
    }
  }
  if (!customElements.get('shopable-video')) customElements.define('shopable-video', ShopableVideo);

  /* --- Lightbox controller: vertical reel, one active video plays, mute/play/progress/nav/close. --- */
  class SvLightbox {
    constructor(root, index, returnFocus, autoplay, autoAdvance, sound, loop) {
      this.root = root;
      root._svLightbox = this;   // back-ref so open() can tear down a still-open reel (mini-player) before replacing it
      this.returnFocus = returnFocus;
      this.wantPlay = !!autoplay; // Autoplay setting: true = play on open/scroll; false = show the play button
      this.autoAdvance = !!autoAdvance; // when a video ends, scroll to the next reel (loops back to the first after the last)
      this.loop = !!loop; // "Infinite loop" setting: swipe/arrow past the last video -> wraps to the first (and before the first -> the last), on desktop + mobile
      this.reel = root.querySelector('[data-sv-reel]');
      this.slides = Array.prototype.slice.call(root.querySelectorAll('.sv-reel__slide'));
      // "Play the reel with sound" setting: OFF -> muted autoplay (the one mode browsers ALWAYS allow -> plays instantly, no stuck spinner; viewer taps the speaker to unmute). ON -> try with sound (open was a click), the activate() catch falls back to muted if the browser blocks it.
      this.muted = !sound;
      this.active = -1;
      this.isMobile = window.matchMedia('(max-width: 749px)').matches;
      // Save-Data / slow (2g/3g) connections: skip neighbour prefetch, keeping the bandwidth-frugal lazy load.
      var _conn = navigator.connection; this._saveData = !!(_conn && (_conn.saveData || /\b[23]g\b/.test(_conn.effectiveType || '')));
      // mini = docked corner player (open() decided per the device setting); otherwise fullscreen.
      // miniOk = mini is allowed on this device -> the minimize/expand toggle is available in both states.
      this.miniOk = root.classList.contains('sv-mini-ok');
      this.mini = root.classList.contains('sv-lightbox--mini');

      // lock page scroll only in fullscreen; the corner mini-player leaves the page usable
      if (!this.mini) document.body.style.overflow = 'hidden';

      // Splide (vertical, ttb) drives the reel: ONE video per swipe. Build the Splide DOM (track+list) around the slides,
      // create the instance now, mount inside the is-open frame below (after every on() hook). Custom vertical transform
      // engine (pos/apply/animateTo/slideTo/_slideH/_reelWrapReset + the pointer drag) is removed -- Splide owns the drag/snap.
      var svt = document.createElement('div'); svt.className = 'splide__track';
      this.list = document.createElement('div'); this.list.className = 'splide__list';
      while (this.reel.firstChild) this.list.appendChild(this.reel.firstChild);
      this.slides.forEach(function (s) { s.classList.add('splide__slide'); });
      svt.appendChild(this.list); this.reel.appendChild(svt); this.reel.classList.add('splide');
      var self = this;
      this.splide = new Splide(this.reel, {
        direction: 'ttb', height: '100%', perPage: 1, perMove: 1, arrows: false, pagination: false,
        wheel: true, wheelMinThreshold: 8, wheelSleep: 250, releaseWheel: true,   // native mouse-wheel/trackpad nav (one video per gesture; handles trackpad inertia via threshold+sleep). releaseWheel:true => at a non-loop reel's first/last video the wheel releases to native page scroll instead of being swallowed (matters for the corner mini, where the page stays scrollable; fullscreen locks body anyway)
        drag: true, flickMaxPages: 1, waitForTransition: true,
        speed: 320, easing: 'cubic-bezier(.22,.61,.36,1)',   // built-in reducedMotion => speed 0 (jump between reels, no glide)
        type: this.loop ? 'loop' : 'slide', start: index, keyboard: false, slideFocus: false, // loop => Splide clones give a SEAMLESS wrap; clone <video>s stay poster-only (this.slides = the pre-mount reals, so activate() never touches a clone => no dup playback/loading). Non-loop => plain slide, clamps at the ends.
      });
      this.splide.on('active', function () { self.activate(self.splide.index); });   // active slide changed -> play its video, pause + reset the rest
      // is-open + mount in the SAME frame: is-open gives the reel its full height FIRST, THEN Splide mounts & measures (ttb collapses to 0-height slides if it measures before the reel is laid out). Mount fires the initial 'active' for start:index -> activate() plays the clicked slide.
      requestAnimationFrame(() => { this.root.classList.add('is-open'); if (this.splide) this.splide.mount(); this.syncAllMute(); });   // after mount the loop clones exist -> put every mute icon on the reel's sound state up-front (no wrong-icon flash on the first video or when navigating)

      // per-slide controls (event delegation)
      root.addEventListener('click', (e) => this.onClick(e));
      var prev = root.querySelector('[data-sv-reel-prev]'), next = root.querySelector('[data-sv-reel-next]');
      if (prev) prev.addEventListener('click', () => this.go(-1));
      if (next) next.addEventListener('click', () => this.go(1));
      // add-to-cart + minimize are handled by the delegated onClick above -- per-button wiring here missed
      // the buttons inside Splide's ttb loop clones (created at mount, after wiring), leaving them dead
      // fullscreen only: tap the dark backdrop to close (mini-player has no backdrop).
      // 'click' (not 'mousedown') so a DRAG that starts on the dark area scrolls the reel instead of
      // closing it — Splide's Drag component swallows the click that ends a real drag, so post-drag
      // clicks never land here.
      root.addEventListener('click', (e) => { if (!this.mini && (e.target === this.root || e.target === this.reel || e.target.classList.contains('sv-reel__slide') || e.target.classList.contains('sv-reel__stage'))) this.close(); });
      // desktop mini-player: drag by the top handle to reposition
      var handle = root.querySelector('[data-sv-handle]');
      if (handle) {
        var drag = false, sx, sy, ox, oy, rw, rh;
        handle.addEventListener('pointerdown', (e) => {
          drag = true; try { handle.setPointerCapture(e.pointerId); } catch (_) {}
          var r = root.getBoundingClientRect();
          root.style.right = 'auto'; root.style.bottom = 'auto'; root.style.left = r.left + 'px'; root.style.top = r.top + 'px';
          sx = e.clientX; sy = e.clientY; ox = r.left; oy = r.top; rw = r.width; rh = r.height;   // size cached ONCE -- reading offsetWidth/Height inside pointermove forced a synchronous layout every frame of the drag
          e.preventDefault();
        });
        handle.addEventListener('pointermove', (e) => {
          if (!drag) return;
          var nx = Math.max(4, Math.min(window.innerWidth - rw - 4, ox + (e.clientX - sx)));
          var ny = Math.max(4, Math.min(window.innerHeight - rh - 4, oy + (e.clientY - sy)));
          root.style.left = nx + 'px'; root.style.top = ny + 'px';
        });
        var endDrag = (e) => { drag = false; try { handle.releasePointerCapture(e.pointerId); } catch (_) {} };
        handle.addEventListener('pointerup', endDrag); handle.addEventListener('pointercancel', endDrag);
      }

      // mouse-wheel / trackpad -> NATIVE Splide wheel option (wheel/wheelMinThreshold/wheelSleep in the reel config above)

      // keyboard navigation removed per merchant request (no Escape/arrows/Space, no Tab focus-trap, no focus-into-dialog)
      this.root.setAttribute('tabindex', '-1'); this.reel.setAttribute('tabindex', '-1');
      // re-fit the ttb height when the viewport changes. Coalesce bursts (mobile URL-bar show/hide,
      // orientation, desktop edge-drag) into ONE refresh per frame, and skip while a swipe is in flight
      // (state 3 = IDLE) so refresh() never fights a move — Splide's own height-aware Layout relayout is
      // the backstop if a tick is skipped. The pending frame no-ops after teardown (guarded on this.splide).
      this._onReelResize = () => {
        if (this._reelResizeRaf) return;
        this._reelResizeRaf = requestAnimationFrame(() => {
          this._reelResizeRaf = 0;
          if (this.splide && this.splide.state.is(3)) this.splide.refresh();
        });
      };
      window.addEventListener('resize', this._onReelResize);

      // Hidden tab: pause the active video — with autoadvance + loop the reel otherwise cycles ALL
      // videos indefinitely in a background tab (re-downloading clips, playing audio if unmuted).
      // The play intent is restored when the tab becomes visible again.
      this._onVis = () => {
        var v = this.slides[this.active] && this.slides[this.active].querySelector('.sv-reel__video');
        if (!v) return;
        if (document.hidden) { this._resumeOnShow = !v.paused; v.pause(); }
        else if (this._resumeOnShow) { this._resumeOnShow = false; var p = v.play(); if (p && p.catch) p.catch(function () {}); }
      };
      document.addEventListener('visibilitychange', this._onVis);
    }

    activate(i) {
      if (i < 0 || i === this.active) return;
      this.active = i;
      this.slides.forEach((slide, idx) => {
        var v = slide.querySelector('.sv-reel__video');
        if (!v) return;
        if (idx === i) {
          if (!v.src && v.dataset.src) { v.src = v.dataset.src; }
          this.bind(v);
          if (this.wantPlay) {                       // Autoplay ON -- play right away
            v.muted = this.muted;
            this.setPaused(v, false);
            var p = v.play();
            if (p && p.catch) p.catch((err) => {      // play() rejected
              if (err && err.name === 'AbortError') return;   // a fast video change paused this video mid-play() -> that's an interruption, NOT an autoplay-with-sound block. Muting here (the old behaviour) flipped the sound off on every switch, so the mute icon flickered off/on. Leave the sound intent untouched.
              this.muted = true; v.muted = true;      // genuine autoplay-with-sound block (NotAllowedError) -> fall back to muted + retry
              var p2 = v.play(); if (p2 && p2.catch) p2.catch(() => this.setPaused(v, true));
              this.syncAllMute();
            });
            this.syncAllMute();
          } else {                                    // Autoplay OFF -- show the centre play button, wait for a click (idle: NOT loading, so no spinner)
            v.pause();
            this.setPaused(v, true);
            v.muted = this.muted;                     // a fresh <video> defaults to muted=false — align it with the reel's sound state FIRST, or the idle mute icon flips to "unmuted" on every never-played slide
            this.syncMute(v);
            var wIdle = v.closest('.sv-reel__video-wrap');
            if (wIdle) { wIdle.removeAttribute('data-loading'); wIdle.setAttribute('data-idle', ''); }   // hide the spinner + show the persistent play button
          }
        } else {
          v.pause();
          try { v.currentTime = 0; } catch (e) {}
        }
      });
      this.updateNav();
      this._preloadNeighbors(i);
    }

    // TikTok-style prefetch: buffer the clips on either side of the active one (bounded to ±1, loop-aware)
    // so the next swipe lands on an already-buffered video -> plays instantly, no loading spinner. Progressive
    // (only clips near the viewer's position ever load), so total buffering stays bounded. Skipped on Save-Data.
    _preloadNeighbors(i) {
      if (this._saveData) return;
      var n = this.slides.length; if (n < 2) return;
      var idxs = this.loop ? [(i + 1) % n, (i - 1 + n) % n] : [i + 1, i - 1];
      idxs.forEach((k) => {
        if (k === i || k < 0 || k >= n) return;
        var slide = this.slides[k]; if (!slide) return;
        var v = slide.querySelector('.sv-reel__video'); if (!v) return;
        if (!v.src && v.dataset.src) { v.preload = 'auto'; v.src = v.dataset.src; }   // markup ships preload="none"; flip to auto + set src so it buffers ahead
      });
    }

    bind(v) {
      if (v._svBound) return; v._svBound = true;
      var wrap = v.closest('.sv-reel__video-wrap');
      var bar = wrap.querySelector('[data-sv-progress]');
      if (v.readyState < 2) wrap.setAttribute('data-loading', '');   // a PREFETCHED neighbour is already buffered (readyState >= HAVE_CURRENT_DATA) -> no spinner on activate (TikTok-style instant swipe)
      v.addEventListener('loadeddata', () => wrap.removeAttribute('data-loading'));
      v.addEventListener('playing', () => { wrap.removeAttribute('data-loading'); this.setPaused(v, false); });
      v.addEventListener('waiting', () => wrap.setAttribute('data-loading', ''));
      v.addEventListener('pause', () => { wrap.removeAttribute('data-loading'); this.setPaused(v, true); });   // a paused video isn't loading -- hide the spinner (else it spins forever if you pause mid-buffer)
      v.addEventListener('play', () => { this.setPaused(v, false); wrap.removeAttribute('data-idle'); });   // once it starts, drop the idle play-button state
      v.addEventListener('canplay', () => wrap.removeAttribute('data-loading'));   // enough buffered to play -> stop the spinner
      v.addEventListener('error', () => wrap.removeAttribute('data-loading'));     // failed to load -> stop the spinner rather than spin forever
      v.addEventListener('timeupdate', () => { if (bar && v.duration) bar.style.transform = 'scaleX(' + (v.currentTime / v.duration) + ')'; });   // scaleX = compositor-only; animating width forced a layout on every timeupdate tick while the video played
      if (this.autoAdvance) {
        v.loop = false;                                     // override the loop attr so 'ended' can fire
        v.addEventListener('ended', () => {
          if (!this.autoAdvance) return;
          var cur = this.slides[this.active] && this.slides[this.active].querySelector('.sv-reel__video');
          if (cur !== v) return;                            // only the video the viewer is watching drives the advance
          this.wantPlay = true;                             // a video just finished -> keep the reel playing so the next one auto-plays; else activate() takes its wantPlay===false branch and shows the centre play button (paused). Muted state is inherited + the muted-retry in activate() covers the autoplay policy.
          if (this.active >= this.slides.length - 1) { if (this.loop) this.go(1); else if (this.splide) this.splide.go(0); } // last video ended -> loop ON: seamless wrap forward to the first (go's relative '>' + Splide loop clones); loop OFF: jump back to the first
          else this.go(1);                                  // otherwise advance to the next reel; activate() plays it
        });
      }
    }
    setPaused(v, paused) { var w = v.closest('.sv-reel__video-wrap'); if (w) { if (paused) w.setAttribute('data-paused', ''); else w.removeAttribute('data-paused'); } }
    syncMute(v) { var w = v.closest('.sv-reel__video-wrap'); if (!w) return; var b = w.querySelector('[data-sv-mute]'); if (b) { if (v.muted) b.removeAttribute('data-unmuted'); else b.setAttribute('data-unmuted', ''); } }
    syncAllMute() { var on = !this.muted; this.root.querySelectorAll('[data-sv-mute]').forEach(function (b) { if (on) b.setAttribute('data-unmuted', ''); else b.removeAttribute('data-unmuted'); }); }   // keep EVERY slide's mute icon (incl. Splide loop clones) on the reel's single mute state, so a freshly-shown video never flashes the wrong icon before activate() runs

    onClick(e) {
      if (e.target.closest('[data-sv-close]')) { this.close(); return; }   // close is now per-slide (one in each video-wrap), so handle it via delegation instead of wiring a single button
      var mini = e.target.closest('[data-sv-minimize]');
      if (mini) { e.preventDefault(); e.stopPropagation(); this.toggleMini(); return; }
      var add = e.target.closest('[data-sv-add]');
      if (add) { e.preventDefault(); e.stopPropagation(); this.addToCart(add); return; }
      var mute = e.target.closest('[data-sv-mute]');
      var pp = e.target.closest('[data-sv-playpause]');
      var vid = e.target.closest('.sv-reel__video');
      var v = this.slides[this.active] && this.slides[this.active].querySelector('.sv-reel__video');
      if (!v) return;
      if (mute) { e.stopPropagation(); v.muted = !v.muted; this.muted = v.muted; this.syncAllMute(); return; }
      if (pp || vid) { e.preventDefault(); var wasPaused = v.paused; if (wasPaused) { v.muted = this.muted; this.wantPlay = true; v.play().catch(() => {}); } else { v.pause(); this.wantPlay = false; } this.syncMute(v); this._flashPP(v, wasPaused ? 'play' : 'pause'); }
    }
    _flashPP(v, action) {   // TikTok/YouTube-style tap feedback: flash the play/pause icon ONCE on toggle, then it vanishes (no hover reveal, no persistent button)
      var w = v.closest('.sv-reel__video-wrap'); if (!w) return;
      var btn = w.querySelector('[data-sv-playpause]'); if (!btn) return;
      btn.removeAttribute('data-flash');
      void btn.offsetWidth;   // reflow so the animation restarts even on rapid taps
      btn.setAttribute('data-flash', action);
      var done = function () { btn.removeAttribute('data-flash'); btn.removeEventListener('animationend', done); };
      btn.addEventListener('animationend', done);
    }

    go(dir) {   // relative nav: with type:'loop' Splide wraps SEAMLESSLY in the swipe direction (through the clones, shortest path); with type:'slide' it clamps at the first/last. Splide's 'active' event then drives activate().
      if (this.splide) this.splide.go(dir > 0 ? '>' : '<');
    }
    updateNav() {
      var prev = this.root.querySelector('[data-sv-reel-prev]'), next = this.root.querySelector('[data-sv-reel-next]');
      if (prev) prev.disabled = !this.loop && this.active <= 0;                          // loop ON -> arrows never dead-end
      if (next) next.disabled = !this.loop && this.active >= this.slides.length - 1;
    }
    toggleMini() {
      if (!this.miniOk) return;                                   // mini isn't allowed on this device -> no toggle (button is hidden too)
      var goMini = !this.root.classList.contains('sv-lightbox--mini');
      this.root.classList.toggle('sv-lightbox--mini', goMini);
      this.mini = goMini;
      document.body.style.overflow = this.mini ? '' : 'hidden';   // mini leaves the page usable
      var active = this.active;                                    // reel dimensions change (fullscreen <-> corner) -> let Splide re-fit its ttb height + re-align
      requestAnimationFrame(() => { if (this.splide) { this.splide.refresh(); this.splide.go(active); } });
    }

    addToCart(btn) {
      if (btn.hasAttribute('aria-busy')) return;
      var id = btn.getAttribute('data-variant-id'); if (!id) return;
      var cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      var routeRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) ? Shopify.routes.root : '/';
      var body = { id: id, quantity: 1 };
      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        try { body.sections = cartEl.getSectionsToRender().map(function (s) { return s.id; }); body.sections_url = window.location.pathname; } catch (e) {}
      }
      btn.setAttribute('aria-busy', 'true');
      fetch(routeRoot + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' }, body: JSON.stringify(body) })
        .then(function (r) { return r.json(); })
        .then((res) => {
          btn.removeAttribute('aria-busy');
          if (res && res.status) { btn.classList.add('sv-add--err'); setTimeout(function () { btn.classList.remove('sv-add--err'); }, 1600); return; }
          if (cartEl && typeof cartEl.renderContents === 'function') {
            // renderContents() OPENS the cart notification/drawer — under the fullscreen reel
            // (z-index 10000, opaque backdrop) it opened INVISIBLY with focus trapped inside it.
            // Dock to the corner mini when the device allows it (the cart UI layers above the
            // low-z mini), otherwise close the reel, THEN let the cart UI open on top.
            if (!this.mini) { if (this.miniOk) this.toggleMini(); else this.close(); }
            try { cartEl.renderContents(res); } catch (e) {}
          }
          else { document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true })); }
          btn.classList.add('sv-add--ok'); setTimeout(function () { btn.classList.remove('sv-add--ok'); }, 1600);
        })
        .catch(() => { btn.removeAttribute('aria-busy'); });
    }

    // Release resources: destroy the Splide instance, drop its window 'resize' listener, pause every video.
    // Shared by close() and by open()'s replace-existing path so re-opening can never leak an instance/listener.
    teardown() {
      if (this.splide) { try { this.splide.destroy(true); } catch (e) {} this.splide = null; }   // completely=true: instance is discarded, a fresh one is built per open
      if (this._onReelResize) { window.removeEventListener('resize', this._onReelResize); this._onReelResize = null; }
      if (this._onVis) { document.removeEventListener('visibilitychange', this._onVis); this._onVis = null; }
      this.slides.forEach((s) => { var v = s.querySelector('.sv-reel__video'); if (v) v.pause(); });
    }
    close() {
      this.root.classList.remove('is-open');
      this.teardown();
      document.body.style.overflow = '';
      // Resume the host section's own preview (open() paused it while the reel covered the page).
      var host = this.root._svHost;
      if (host && host._svResumeHv) { try { host._svResumeHv.preview(true); } catch (e) {} host._svResumeHv = null; }
      var stVid = document.querySelector('.sv-sticky.is-visible .sv-sticky__video');
      if (stVid) { var sp = stVid.play(); if (sp && sp.catch) sp.catch(function () {}); }
      if (this.returnFocus && this.returnFocus.focus) { try { this.returnFocus.focus(); } catch (e) {} }
      setTimeout(() => { this.root.remove(); }, 320);
    }
  }
})();
